/**
 * UserMenu — Header user dropdown (profile, branch, theme, logout)
 * Extracted from App.js as part of AppRouter refactor.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { API_BASE } from '../../lib/api';
const API = API_BASE;
import { ChevronDown, User, LogOut, KeyRound, Search, Eye, EyeOff } from 'lucide-react';
import { useERPContextSafe } from '@/context/ERPContext';
import { useSecurityGuard } from '@/security/SecurityIntegration';


function StatusChip({ ok, label }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: '2px 7px', borderRadius: 100, fontSize: 9, fontWeight: 700,
      background: ok ? '#f0fdf4' : '#fef2f2',
      color: ok ? '#16a34a' : '#dc2626',
      border: `1px solid ${ok ? '#bbf7d0' : '#fecaca'}`,
    }}>
      <span style={{ width: 4, height: 4, borderRadius: '50%', background: ok ? '#10b981' : '#ef4444', display: 'inline-block' }} />
      {label}
    </span>
  );
}

const THEME_SWATCHES = [
  { id: 'default', label: 'Default (White)', header: '#ffffff' },
  { id: 'ex-dark-blue', label: 'Ex-Dark blue', header: '#0a1628' },
  { id: 'dark-blue', label: 'Dark blue', header: '#1a365d' },
  { id: 'lite-blue', label: 'Lite Blue', header: '#3b82f6' },
  { id: 'hash', label: 'Hash', header: '#4a5568' },
  { id: 'light-hash', label: 'Light Hash', header: '#9ca3af' },
  { id: 'blue', label: 'Blue', header: '#3b5998' },
];

export const HeaderUserInfo = ({ onClick, isOpen, theme }) => {
  const isDark = theme === 'dark';
  const erpCtx = useERPContextSafe();
  const initial = (erpCtx.username || 'A').charAt(0).toUpperCase();
  const displayName = erpCtx.username || 'Admin';
  return (
    <button data-testid="user-menu-btn" onClick={onClick}
      className={`flex items-center gap-2 px-2 py-1 rounded-lg transition-colors ${isOpen ? (isDark ? 'bg-white/10' : 'bg-slate-100') : isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold bg-blue-600 text-white`}>{initial}</div>
      <span className={`text-[11px] font-semibold hidden sm:inline uppercase ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{displayName}</span>
      <ChevronDown size={12} className={isDark ? 'text-slate-500' : 'text-slate-400'} />
    </button>
  );
};

const UserMenu = ({ isOpen, onClose, onLogout, theme, headerTheme, onThemeChange }) => {
  const isDark = theme === 'dark';
  const ref = useRef(null);
  const erpCtx = useERPContextSafe();
  const { verified, mssqlConnected } = useSecurityGuard();
  const [showPwdTool, setShowPwdTool] = useState(false);
  const [pwdUsername, setPwdUsername] = useState('');
  const [pwdResult, setPwdResult] = useState(null);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [showPlain, setShowPlain] = useState(false);

  const isLicensor = ['LICENSOR', 'SUPER_ADMIN', 'ADMINSUPPORT'].includes(erpCtx.role || '');

  const lookupPassword = useCallback(async () => {
    if (!pwdUsername.trim()) return;
    setPwdLoading(true); setPwdError(''); setPwdResult(null); setShowPlain(false);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const res = await fetch(`${API}/api/auth/user-credential?username=${encodeURIComponent(pwdUsername.trim())}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!data.success) { setPwdError(data.message || 'Not found'); }
      else { setPwdResult(data); }
    } catch (e) { setPwdError(e.message); }
    setPwdLoading(false);
  }, [pwdUsername]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  const displayName = erpCtx.username || 'Admin User';
  const displayEmail = erpCtx.company?.email || erpCtx.email || '';
  const displayRole = erpCtx.role || '';
  const displayCompany = erpCtx.company?.compName || '';
  return (
    <div ref={ref} data-testid="user-menu-dropdown" data-dropdown-panel className={`user-menu-panel absolute right-0 top-full mt-2 w-72 border rounded-xl shadow-2xl z-[200] overflow-hidden ${isDark ? 'bg-[#1a1a1a] border-[#333]' : ''}`}>
      <div className={`p-4 border-b ${isDark ? 'border-[#333] bg-[#151515]' : 'border-slate-100 bg-slate-50'}`}>
        <p data-testid="user-display-name" className={`text-sm font-bold uppercase ${isDark ? 'text-white' : 'text-slate-900'}`}>{displayName}</p>
        {displayCompany && <p className={`text-[10px] font-semibold mt-0.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{displayCompany}</p>}
        <p className={`text-[10px] font-mono ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{displayEmail || displayRole}</p>
      </div>
      {erpCtx.currentBranch && (
        <div className={`px-4 py-2 border-b ${isDark ? 'border-[#333]' : 'border-slate-100'}`}>
          <p className={`text-[9px] font-bold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Branch</p>
          <p className={`text-[11px] font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{erpCtx.currentBranch.name} {erpCtx.currentBranch.location ? `- ${erpCtx.currentBranch.location}` : ''}</p>
        </div>
      )}
      <div className="p-1">
        <div className={`px-3 py-2 border-b ${isDark ? 'border-[#333]' : 'border-slate-100'}`}>
          <p className={`text-[9px] font-bold uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Change Theme</p>
          <div className="flex gap-1.5 flex-wrap">
            {THEME_SWATCHES.map(t => (
              <button key={t.id} onClick={() => { onThemeChange(t.id); }} title={t.label}
                className={`w-6 h-6 rounded-full border-2 transition-all ${headerTheme === t.id ? 'border-blue-500 ring-2 ring-blue-400 scale-110' : 'border-slate-300 hover:scale-105'}`}
                style={{ background: t.header }} data-testid={`theme-${t.id}`} />
            ))}
          </div>
        </div>
        <button data-testid="user-profile-btn" className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${isDark ? 'text-slate-400 hover:bg-white/5 hover:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}>
          <User size={14} /> My Profile
        </button>

        {/* Password Lookup Tool — visible to LICENSOR / SUPER_ADMIN only */}
        {isLicensor && (
          <div className={`border-t ${isDark ? 'border-[#333]' : 'border-slate-100'}`}>
            <button data-testid="pwd-lookup-btn" onClick={() => { setShowPwdTool(v => !v); setPwdResult(null); setPwdError(''); setPwdUsername(''); }}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${isDark ? 'text-amber-400 hover:bg-amber-400/10' : 'text-amber-600 hover:bg-amber-50'}`}>
              <KeyRound size={14} /> Password Lookup
              <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded font-bold ${isDark ? 'bg-amber-400/20 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>LICENSOR</span>
            </button>
            {showPwdTool && (
              <div className={`mx-2 mb-2 p-3 rounded-lg border ${isDark ? 'bg-[#111] border-[#333]' : 'bg-amber-50 border-amber-200'}`}>
                <p className={`text-[9px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>Decrypt User Password</p>
                <div className="flex gap-1.5">
                  <input data-testid="pwd-lookup-input" value={pwdUsername} onChange={e => setPwdUsername(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && lookupPassword()}
                    placeholder="Enter username..."
                    className={`flex-1 text-[11px] px-2 py-1.5 rounded border outline-none ${isDark ? 'bg-[#1a1a1a] border-[#444] text-white placeholder-slate-600' : 'bg-white border-amber-200 text-slate-800 placeholder-slate-400'}`} />
                  <button data-testid="pwd-lookup-go" onClick={lookupPassword} disabled={pwdLoading}
                    className={`px-2.5 py-1.5 rounded text-[10px] font-bold transition-colors ${isDark ? 'bg-amber-500 text-black hover:bg-amber-400' : 'bg-amber-500 text-white hover:bg-amber-600'}`}>
                    {pwdLoading ? '...' : <Search size={11} />}
                  </button>
                </div>
                {pwdError && <p className="text-[10px] text-red-400 mt-1.5">{pwdError}</p>}
                {pwdResult && (
                  <div className={`mt-2 p-2 rounded border text-[10px] ${isDark ? 'bg-[#0d0d0d] border-[#333]' : 'bg-white border-amber-200'}`}>
                    <p className={`font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-800'}`}>{pwdResult.username} <span className={`ml-1 font-normal ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>({pwdResult.source})</span></p>
                    {pwdResult.email && <p className={`mb-1 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{pwdResult.email}</p>}
                    <div className="flex items-center gap-1.5">
                      <span className={`font-mono font-bold tracking-wider ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                        {showPlain ? pwdResult.decryptedPassword : '••••••••'}
                      </span>
                      <button onClick={() => setShowPlain(v => !v)} className={`ml-auto p-0.5 rounded ${isDark ? 'text-slate-500 hover:text-white' : 'text-slate-400 hover:text-slate-700'}`}>
                        {showPlain ? <EyeOff size={11} /> : <Eye size={11} />}
                      </button>
                      <button onClick={() => { navigator.clipboard.writeText(pwdResult.decryptedPassword); }}
                        className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${isDark ? 'bg-blue-600/30 text-blue-300 hover:bg-blue-600/50' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'}`}>
                        Copy
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className={`px-3 py-2 border-t ${isDark ? 'border-[#333]' : 'border-slate-100'}`}>
          <p className={`text-[9px] font-bold uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>System Status</p>
          <div style={{ display: 'flex', gap: 6 }}>
            <StatusChip ok={verified} label={verified ? 'DNA ✓' : 'DNA ✗'} />
            <StatusChip ok={mssqlConnected} label={mssqlConnected ? 'DB Online' : 'DB Offline'} />
          </div>
        </div>
        <button data-testid="logout-btn" onClick={onLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium text-red-500 hover:bg-red-500/10 transition-colors">
          <LogOut size={14} /> Sign Out
        </button>
      </div>
    </div>
  );
};

export default UserMenu;
